
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, CheckCircle, LogOut, Settings, ChevronDown, Shield, Clock, Key, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { useWallet } from '../../../hooks/useWallet';
import { Button } from '../../ui/Button';
import { WalletIcon } from '../../ui/ChainIcons';

export const WalletSettingsModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const {
        walletAddress,
        walletType,
        disconnect,
        balances,
        preferredChain,
        setPreferredChain,
        defaultTokenMint,
        setDefaultTokenMint
    } = useWallet();
    
    const [copied, setCopied] = useState(false);
    const [autoLock, setAutoLock] = useState('15');
    const [showSeed, setShowSeed] = useState(false);
    const [seedRevealed, setSeedRevealed] = useState(false);
    const [copiedSeed, setCopiedSeed] = useState(false);

    // Mock mnemonic seed
    const MOCK_SEED = ["ghost", "protocol", "sovereign", "private", "ledger", "atomic", "vault", "silent", "titanium", "encryption", "mainnet", "consensus"];

    const handleCopy = () => {
        if (walletAddress) {
            navigator.clipboard.writeText(walletAddress);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };
    
    const handleDisconnect = () => {
        disconnect();
        onClose();
    };

    const preferredChainTokens = React.useMemo(() => {
        return balances.filter(b => b.chain === preferredChain);
    }, [balances, preferredChain]);

    const handleCopySeed = () => {
        navigator.clipboard.writeText(MOCK_SEED.join(' '));
        setCopiedSeed(true);
        setTimeout(() => setCopiedSeed(false), 2000);
    };

    return (
        <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
            onClick={onClose}
        >
          <motion.div 
            initial={{ scale: 0.98, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.98, y: 10 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-[#15171C] rounded-sm shadow-2xl w-full max-w-md relative border border-stone-200 dark:border-[#2D3142] overflow-hidden flex flex-col"
          >
            <div className="p-5 border-b border-stone-100 dark:border-white/5 flex justify-between items-center bg-gray-50 dark:bg-[#0A0B0F]">
                <div className="flex items-center gap-3">
                    <Settings size={18} className="text-stone-400"/>
                    <h2 className="font-serif text-lg text-[#0A0B0F] dark:text-white font-bold">Vault Configuration</h2>
                </div>
                <button onClick={onClose} className="text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors">
                    <X size={20} />
                </button>
            </div>
    
            <div className="p-6 space-y-5 overflow-y-auto no-scrollbar max-h-[75vh]">
                {/* Section: Identity */}
                <div className="space-y-2">
                    <label className="text-[9px] font-black text-stone-400 uppercase tracking-[0.3em] ml-1">Active Identity</label>
                    <div className="flex items-center gap-4 p-4 bg-stone-50 dark:bg-white/[0.02] rounded-sm border border-stone-100 dark:border-white/5">
                        <WalletIcon wallet={walletType || ''} className="w-10 h-10 rounded-sm shadow-sm" />
                        <div className="flex-1 min-w-0">
                            <div className="font-black text-[10px] text-stone-500 dark:text-stone-400 uppercase tracking-widest leading-none mb-1.5">{walletType} Protocol</div>
                            <div className="text-xs font-mono font-bold text-[#0A0B0F] dark:text-white truncate">{walletAddress}</div>
                        </div>
                        <button onClick={handleCopy} className="p-2 text-stone-400 hover:text-nobel-gold transition-colors">
                            {copied ? <CheckCircle size={16} className="text-emerald-500" /> : <Copy size={16}/>}
                        </button>
                    </div>
                </div>

                {/* NEW Section: Recovery Seed */}
                <div className="space-y-2">
                    <label className="text-[9px] font-black text-stone-400 uppercase tracking-[0.3em] ml-1">Sovereign Recovery</label>
                    <div className="p-4 bg-stone-900 rounded-sm border border-white/10 relative overflow-hidden group">
                        <div className="relative z-10 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Key size={18} className="text-nobel-gold" />
                                <div>
                                    <div className="text-[10px] font-black text-white uppercase tracking-widest">Backup Recovery Seed</div>
                                    <div className="text-[8px] text-stone-500 font-bold uppercase tracking-widest mt-1">12-Word Mnemonic Phrase</div>
                                </div>
                            </div>
                            <button 
                                onClick={() => { setSeedRevealed(!seedRevealed); setShowSeed(true); }}
                                className="p-2 rounded-sm bg-white/5 text-white hover:bg-white/10 transition-all border border-white/10"
                            >
                                {seedRevealed ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>

                        <AnimatePresence>
                            {seedRevealed && (
                                <motion.div 
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="mt-6 pt-6 border-t border-white/5"
                                >
                                    <div className="grid grid-cols-3 gap-2">
                                        {MOCK_SEED.map((word, i) => (
                                            <div key={i} className="bg-white/5 rounded-sm p-2 text-center border border-white/5">
                                                <span className="text-[8px] font-mono text-stone-600 block mb-0.5">{i+1}</span>
                                                <span className="text-[11px] font-mono font-bold text-white uppercase tracking-tighter">{word}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-4 flex gap-2">
                                        <Button variant="ghost" size="sm" onClick={handleCopySeed} className="flex-1 h-9 text-[9px] border-white/10 hover:bg-white/5 text-stone-400">
                                            {copiedSeed ? <CheckCircle size={12} className="mr-2 text-emerald-500" /> : <Copy size={12} className="mr-2" />}
                                            {copiedSeed ? 'Copied' : 'Copy Seed'}
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => setSeedRevealed(false)} className="flex-1 h-9 text-[9px] border-white/10 hover:bg-white/5 text-red-400">Hide Phrase</Button>
                                    </div>
                                    <div className="mt-4 p-3 bg-red-500/10 rounded-sm border border-red-500/20 flex items-start gap-3">
                                        <AlertTriangle size={14} className="text-red-500 shrink-0 mt-0.5" />
                                        <p className="text-[9px] text-red-200/70 font-bold leading-relaxed uppercase tracking-tight">
                                            Never share your recovery seed. Anyone with this phrase can access your vault.
                                        </p>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                        
                        <div className="absolute top-0 right-0 p-4 opacity-[0.02] pointer-events-none rotate-12">
                            <Shield size={120} className="text-white" />
                        </div>
                    </div>
                </div>
                
                {/* Section: Network Rules */}
                <div className="space-y-4">
                    <div className="space-y-2">
                        <label className="text-[9px] font-black text-stone-400 uppercase tracking-[0.3em] ml-1">Network & Clearing</label>
                        <div className="grid grid-cols-2 gap-2">
                            {(['SUI', 'SOL'] as const).map(chain => (
                                <button 
                                    key={chain} 
                                    onClick={() => setPreferredChain(chain)} 
                                    className={`py-3 rounded-sm text-[10px] font-black uppercase tracking-widest border transition-all ${
                                        preferredChain === chain 
                                        ? 'bg-[#0A0B0F] dark:bg-white text-white dark:text-black border-transparent shadow-lg' 
                                        : 'bg-white dark:bg-white/5 border-stone-200 dark:border-white/10 text-stone-500 hover:border-stone-400'
                                    }`}
                                >
                                    {chain} Network
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-bold text-stone-600 dark:text-stone-400 mb-1 block">Default Settlement Asset</label>
                        <div className="relative group">
                            <select 
                                value={defaultTokenMint || ''} 
                                onChange={e => setDefaultTokenMint(e.target.value || null)}
                                className="w-full appearance-none p-3.5 bg-stone-50 dark:bg-white/5 text-xs font-bold text-[#0A0B0F] dark:text-white rounded-sm border border-stone-200 dark:border-white/10 focus:border-nobel-gold outline-none cursor-pointer"
                            >
                                <option value="">Auto-Detect Protocol</option>
                                {preferredChainTokens.map(token => (
                                    <option key={token.mint} value={token.mint}>{token.symbol} — {token.name}</option>
                                ))}
                            </select>
                             <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none group-hover:text-nobel-gold transition-colors" />
                        </div>
                    </div>
                </div>

                {/* Section: Security Policies */}
                <div className="space-y-2">
                    <label className="text-[9px] font-black text-stone-400 uppercase tracking-[0.3em] ml-1">Enforcement Policies</label>
                    <div className="divide-y divide-stone-100 dark:divide-white/5 border border-stone-100 dark:border-white/5 rounded-sm overflow-hidden">
                        <div className="flex items-center justify-between p-3.5 bg-white dark:bg-white/[0.01]">
                            <div className="flex items-center gap-3">
                                <Clock size={16} className="text-stone-400"/>
                                <span className="text-[11px] font-bold text-[#0A0B0F] dark:text-stone-300 uppercase tracking-wider">Session Timeout</span>
                            </div>
                            <select 
                                value={autoLock} 
                                onChange={(e) => setAutoLock(e.target.value)}
                                className="bg-transparent text-[10px] font-black text-nobel-gold outline-none text-right cursor-pointer uppercase tracking-widest"
                            >
                                <option value="5">5 Minutes</option>
                                <option value="15">15 Minutes</option>
                                <option value="30">30 Minutes</option>
                                <option value="never">Never</option>
                            </select>
                        </div>
                        <div className="flex items-center justify-between p-3.5 bg-white dark:bg-white/[0.01]">
                            <div className="flex items-center gap-3">
                                <Shield size={16} className="text-stone-400"/>
                                <span className="text-[11px] font-bold text-[#0A0B0F] dark:text-stone-300 uppercase tracking-wider">Hardware Enclave</span>
                            </div>
                            <div className="px-2 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-500 text-[8px] font-black uppercase tracking-widest border border-emerald-500/20">Active</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-[#0A0B0F] border-t border-stone-100 dark:border-white/5 flex gap-2">
                <Button onClick={handleDisconnect} variant="outline" className="flex-1 h-11 border-stone-200 dark:border-white/10 text-red-500 font-black text-[10px]" icon={<LogOut size={14}/>}>Terminate</Button>
                <Button onClick={onClose} variant="secondary" className="flex-1 h-11 font-black text-[10px]">Commit Changes</Button>
            </div>
          </motion.div>
        </motion.div>
    );
};
